import React, { useState, useEffect } from 'react';
import { X, Users, Save, AlertTriangle, CheckCircle } from 'lucide-react';
import { roleService } from '../../../services/roleService';

export default function BulkPermissionModal({ onClose, onApply, allRoles }) {
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customPermissions, setCustomPermissions] = useState({});
  const [mode, setMode] = useState('template'); // 'template' or 'custom'
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [validationResults, setValidationResults] = useState([]);
  const [features, setFeatures] = useState([]);
  const [accessLevels, setAccessLevels] = useState([]);

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev =>
      prev?.includes(roleId)
        ? prev?.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handlePermissionChange = (feature, level) => {
    setCustomPermissions(prev => ({
      ...prev,
      [feature]: level
    }));
  };

  const handleValidate = async () => {
    if (selectedRoles?.length === 0) {
      setError('Please select at least one role');
      return;
    }

    try {
      setValidating(true);
      setError('');
      setValidationResults([]);

      let permissionsToValidate = [];

      if (mode === 'template' && selectedTemplate) {
        // Convert template data to permissions array
        permissionsToValidate = Object.entries(selectedTemplate?.templateData || {})?.map(([feature, accessLevel]) => ({
          feature,
          accessLevel
        }));
      } else if (mode === 'custom') {
        permissionsToValidate = Object.entries(customPermissions)?.map(([feature, accessLevel]) => ({
          feature,
          accessLevel
        }));
      }

      // Validate permissions for all selected roles
      const results = await roleService?.validateBulkPermissions(
        selectedRoles,
        permissionsToValidate
      );

      setValidationResults(results);

      // Check if any role has validation errors
      const hasErrors = results?.some(r => !r?.isValid);
      if (hasErrors) {
        setError('Some roles have permission conflicts. Please review below.');
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setValidating(false);
    }
  };

  const handleApply = async () => {
    try {
      setLoading(true);
      setError('');

      let permissionsToApply = [];

      if (mode === 'template' && selectedTemplate) {
        // Use template-based bulk assignment with validation
        await roleService?.applyTemplateToRoles(selectedRoles, selectedTemplate?.id);
      } else if (mode === 'custom') {
        permissionsToApply = Object.entries(customPermissions)?.map(([feature, accessLevel]) => ({
          feature,
          accessLevel
        }));
        
        // Use validation-aware bulk assignment
        await roleService?.bulkAssignPermissionsWithValidation(selectedRoles, permissionsToApply);
      }

      onApply();
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            Bulk Permission Assignment
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Role Selection */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <Users className="w-4 h-4 mr-2" />
              Select Roles ({selectedRoles?.length} selected)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {allRoles?.map((role) => (
                <label
                  key={role?.id}
                  className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                    selectedRoles?.includes(role?.id)
                      ? 'border-indigo-500 bg-indigo-50' :'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles?.includes(role?.id)}
                    onChange={() => handleRoleToggle(role?.id)}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium text-gray-900">{role?.displayName}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Mode Tabs */}
          <div className="mb-6">
            <div className="flex space-x-2 mb-4">
              <button
                onClick={() => setMode('template')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'template' ?'bg-indigo-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Template
              </button>
              <button
                onClick={() => setMode('custom')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  mode === 'custom' ?'bg-indigo-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Custom
              </button>
            </div>

            {mode === 'template' && selectedTemplate && (
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Template</h4>
                <p className="text-sm text-gray-600">{selectedTemplate?.name}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedTemplate?.description}</p>
              </div>
            )}

            {mode === 'custom' && (
              <div className="bg-gray-50 p-4 rounded-md mb-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Custom Permissions</h4>
                <div className="space-y-3">
                  {features?.map((feature) => (
                    <div key={feature?.id} className="flex items-center justify-between p-3 bg-white rounded-md border">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{feature?.name}</p>
                        <p className="text-xs text-gray-500">{feature?.description}</p>
                      </div>
                      <select
                        value={customPermissions?.[feature?.id] || 'none'}
                        onChange={(e) => handlePermissionChange(feature?.id, e?.target?.value)}
                        className="ml-4 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {accessLevels?.map((level) => (
                          <option key={level?.id} value={level?.id}>
                            {level?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Validation Results */}
          {validationResults?.length > 0 && (
            <div className="p-6 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Validation Results</h4>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {validationResults?.map((result, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      result?.isValid
                        ? 'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start">
                      {result?.isValid ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 mr-3 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          result?.isValid ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {result?.roleName}
                        </p>
                        {!result?.isValid && result?.conflicts?.length > 0 && (
                          <ul className="mt-2 space-y-1 text-sm text-red-800">
                            {result?.conflicts?.map((conflict, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                <span>
                                  {conflict?.feature1} ({conflict?.accessLevel1}) ↔{' '}
                                  {conflict?.feature2} ({conflict?.accessLevel2}): {conflict?.description}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleValidate}
            disabled={validating || selectedRoles?.length === 0}
            className="px-4 py-2 border border-indigo-600 rounded-md text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50 disabled:opacity-50"
          >
            {validating ? 'Validating...' : 'Validate'}
          </button>
          <button
            onClick={handleApply}
            disabled={
              loading || 
              selectedRoles?.length === 0 || 
              validationResults?.some(r => !r?.isValid)
            }
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Applying...' : 'Apply to Selected Roles'}
          </button>
        </div>
      </div>
    </div>
  );
}